// PhotoManager.jsx

import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Buffer } from "buffer";

import { Box, Button } from "@metrea-ui/v2";
import BorderedSection from "../BorderedSection/BorderedSection";

import { uploadItemPhoto, removeItemPhoto } from "src/slices/baselineSlice";
import { useDialogBox } from "src/hooks/useDialogBox";
import { errorLog } from "src/utils/debugUtils";

const imgPlaceholder = "/No_Image_Available.jpg";

const PhotoManager = ({
	item,
	onPhotoUpdated,
	editable,
	tableName = "baseline_items",
	title = "Photo",
	showHeader = true,
	containerStyle,
}) => {
	const fileInputRef = useRef(null);

	const [selectedFile, setSelectedFile] = useState(null);
	const [previewImage, setPreviewImage] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

	const { showDialog } = useDialogBox();
	const dispatch = useDispatch();

	// Show preview or current image
	useEffect(() => {
		if (selectedFile) {
			setPreviewImage(URL.createObjectURL(selectedFile));
		} else if (item && item.photo && item.photo.data) {
			setPreviewImage(`data:image/*;base64,${Buffer.from(item.photo.data).toString("base64")}`);
		} else {
			setPreviewImage(imgPlaceholder);
		}
		// Cleanup
		return () => {
			if (previewImage && selectedFile) URL.revokeObjectURL(previewImage);
		};
		// eslint-disable-next-line
	}, [item, selectedFile]);

	const onChangeFile = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		if (!/\.(jpe?g|png|gif)$/i.test(file.name)) {
			showDialog({
				title: "Error",
				message: ["Invalid input", " ", "Please choose a valid image (jpg, jpeg, png, gif)."],
				type: "error",
			});

			return;
		}
		setSelectedFile(file);
	};

	const onSavePhoto = async () => {
		if (!selectedFile) return;
		setIsSaving(true);

		try {
			await dispatch(uploadItemPhoto({ id: item.id, file: selectedFile })).unwrap();

			showDialog({
				title: "Success",
				message: ["Photo updated!"],
			});

			setSelectedFile(null);
			// trigger parent to reload data
			onPhotoUpdated && onPhotoUpdated();
		} catch (err) {
			showDialog({
				title: "Error",
				message: ["Photo upload failed."],
				type: "error",
			});

			errorLog(2, "[PhotoManager][FLOW] onSavePhoto failed", { err, itemId: item?.id });
		}
		setIsSaving(false);
	};

	const onRemovePhoto = async () => {
		setIsSaving(true);
		try {
			await dispatch(removeItemPhoto(item.id)).unwrap();

			showDialog({
				title: "Success",
				message: ["Photo removed!"],
			});
			setSelectedFile(null);
			onPhotoUpdated && onPhotoUpdated();
		} catch (err) {
			showDialog({
				title: "Error",
				message: ["Remove failed."],
				type: "error",
			});
			errorLog(2, "[PhotoManager][FLOW] onRemovePhoto failed", { err, itemId: item?.id });
		}
		setIsSaving(false);
	};

	// debugLog(10, "[PhotoManager]", { selectedFile: selectedFile, item: item });

	return (
		<Box display="flex" flex="1" minHeight={0}>
			<BorderedSection
				title={title}
				showHeader={showHeader}
				containerStyle={containerStyle}
			>
				<Box display="flex" flexDirection="column" height="100%">
					<Box mx={20}>
						<img
							alt="item"
							width="250px"
							src={
								previewImage
									? previewImage
									: item?.thumbnail
										? `data:image/*;base64,${Buffer.from(item.thumbnail).toString("base64")}`
										: imgPlaceholder
							}
						/>
					</Box>
					{editable && (
						<Box
							mt="auto"
							// Keep a little breathing room between the preview image and the
							// action row without changing the panel's natural height behavior.
							pt={16}
							display="flex"
							gap={6}
						>
							<Button
								color="primary"
								onClick={() => {
									// Explicitly trigger hidden file input
									fileInputRef.current?.click();
								}}
							>
								{/* Upload Photo */}
								{(item && item.photo && item.photo.data ? "Change" : "Upload") + " Photo"}
								<input
									type="file"
									name="myImage"
									hidden
									accept="image/*"
									disabled={isSaving}
									onChange={onChangeFile}
									ref={fileInputRef}
								/>
							</Button>
							<Button
								color="primary"
								onClick={onSavePhoto}
								disabled={!selectedFile || isSaving}
							>
								Save Photo
							</Button>
							<Button color="secondary" onClick={onRemovePhoto} disabled={isSaving}>
								Remove Photo
							</Button>
						</Box>
					)}
				</Box>
			</BorderedSection>
		</Box>
	);
};

export default PhotoManager;
